import { DataResponse, Merchant } from "shared/models";
import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { v4 as uuidv4 } from "uuid";

export const merchantService = createApi({
  reducerPath: "merchantService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getMerchants: builder.query<
      DataResponse<Merchant[]>,
      {
        page: number;
        limit: number;
        search: string;
        skip_log?: boolean;
        partnerId?: number;
        sortBy?: string;
        order?: string;
        type?: string;
      }
    >({
      query: (params) => ({
        url: `/merchant-management?page=${params?.page}&limit=${params?.limit}&search=${
          params?.search
        }&include=disbursement_config,banks&skip_log=${
          params?.skip_log ? params?.skip_log : false
        }${params?.partnerId ? `&partnerId=${params?.partnerId}` : ""}${
          params?.sortBy ? `&sortBy=${params?.sortBy}` : ""
        }${params?.order ? `&order=${params?.order}` : ""}${
          params?.type ? `&type=${params?.type}` : ""
        }`,
        method: "GET",
        headers: !params?.skip_log
          ? {
              PageName: "Merchant Management",
            }
          : {},
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            ...raw,
            ...{ ...raw.disbursement_config },
          }));

          return { ...rawData, data, labelPaidOn: rawData.disbursement_config?.label };
        }
      },
    }),
    getMerchantsSelect: builder.query<
      any,
      { is_cancelled?: boolean, page?: number; limit?: number; search?: string; skip_log?: boolean; partnerId?: number }
    >({
      query: (params) => ({
        // url: `/merchant-management?include=disbursement_config&type=select`,
        url: `/merchant-management/selection`,
        params,
        method: "GET",
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            name: raw.trading_name,
            status: raw.status,
            // disbursementCurrency: raw?.disbursement_config?.currency,
          }));

          const sort = data.sort((a: any, b: any) => a?.name.localeCompare(b?.name));
          return { data: sort, pagination: rawData.meta };
        }
      },
    }),
    getMerchantDetail: builder.query<DataResponse<any>, { id: string; skip_log?: boolean }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}?skip_log=${
          params?.skip_log ? params?.skip_log : false
        }`,
        method: "GET",
        headers: !params?.skip_log
          ? {
              PageName: "Merchant Management",
            }
          : {},
      }),
    }),
    createMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: "/merchant-management",
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => {
        return {
          url: `/merchant-management/${body.id}`,
          body: body,
          params: { skip_log: body?.skip_log ? body?.skip_log : false },
          method: "PUT",
          headers: {
            PageName: "Merchant Management",
          },
        };
      },
    }),
    updateStatusMerchant: builder.mutation<any, { id: number; status: string, is_cancelled: boolean }>({
      query: (body: any) => ({
        url: `/merchant-management/${body.id}/updateStatus`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateBillingInfo: builder.mutation<
      any,
      {
        id: number;
        amount: number;
        currency: string;
        type: string;
        merchant_id: number;
        active: boolean;
      }
    >({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchant_id}/billing/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    createBillingInfo: builder.mutation<
      any,
      {
        amount: number;
        currency: string;
        type: string;
        merchant_id: number;
        active: boolean;
      }
    >({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchant_id}/billing`,
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getMerchantBilling: builder.query<DataResponse<any>, { id: number }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}/billing`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    deleteMerchant: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body.id}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getPayoutAccountDetail: builder.query<any, { merchantId: number; id: number }>({
      query: (params) => ({
        url: `/merchant-management/${params.merchantId}/payout-account/${params.id}`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getListPayoutAccount: builder.query<any, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `/merchant-management/${merchantId}/payout-account`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updatePayoutAccount: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body.merchantId}/payout-account/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    validateMerchantByPublicKey: builder.mutation<any, { merchant_id: any,public_key: string }>({
      query: (body: any) => ({
        url: "/auth/verify-merchant-by-public_key",
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    savePaymentDesc: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body?.merchant_id}/payment-description`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getOperationType: builder.query<any, void>({
      query: () => ({
        url: `/merchant-management/operation-type`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getBookingLabel: builder.query<any, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `/merchant-management/${merchantId}/booking-label`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    getDetailRefundRequestMerchants: builder.query<any, { id: string }>({
      query: (params) => ({
        url: `/merchant-management/${params?.id}/refund-limit`,
        method: "GET",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
    updateDetailRefundRequestMerchants: builder.mutation<any, any>({
      query: (body: any) => ({
        url: `/merchant-management/${body?.merchant_id}/refund-limit`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Management",
        },
      }),
    }),
  }),
});

export const {
  useGetMerchantDetailQuery,
  useLazyGetMerchantDetailQuery,
  useGetMerchantsQuery,
  useLazyGetMerchantsSelectQuery,
  useUpdateMerchantMutation,
  useCreateMerchantMutation,
  useGetMerchantsSelectQuery,
  useLazyGetMerchantsQuery,
  useDeleteMerchantMutation,
  useUpdateStatusMerchantMutation,
  useUpdateBillingInfoMutation,
  useLazyGetPayoutAccountDetailQuery,
  useGetListPayoutAccountQuery,
  useUpdatePayoutAccountMutation,
  useValidateMerchantByPublicKeyMutation,
  useCreateBillingInfoMutation,
  useGetMerchantBillingQuery,
  useLazyGetMerchantBillingQuery,
  useSavePaymentDescMutation,
  useGetOperationTypeQuery,
  useGetBookingLabelQuery,
  useGetDetailRefundRequestMerchantsQuery,
  useUpdateDetailRefundRequestMerchantsMutation,
} = merchantService;
